import _ from "lodash";

import React, { useEffect, useRef } from "react";

import { FormTextInput } from "UIComponents/InputTextField";
import { Button } from "@toddle-edu/ds-web";

import {
  isNewFamilySignUpFlowEnabled,
  isMiddleNameInSignUpEnabled,
} from "config";
import classes from "./CreateParentAccount.scss";

const PASSWORD_VALIDATION_TEXT =
  "Password should be atleast 8 characters in length";

const CreateAccountParent = props => {
  const {
    renderBackButtonComponent,
    updateInputField,
    data: {
      password,
      email,
      firstName,
      middleName,
      lastName,
      confirmPassword,
      userId,
    } = {},
    customStyle: { headerStyleV2, subTextStyle } = {},
    updateCurrentScreen,

    isFamilyClassInviteRoute,
    isFamilyInviteRoute,
    updateInvitedFamilyData,
    isPwdDisabledInParentSignup,
    backButtonRef,
    renderTradeMarkText,
    isOrgWorkspaceLoginPage = false,
    parentInviteCode,
    invitedFamilyData,
    setAuthRegion,
  } = props;

  // create array of refs for 5 input fields
  const inputRef = useRef([]);
  const buttonRef = useRef(null);
  const totalInputFields = isPwdDisabledInParentSignup
    ? isMiddleNameInSignUpEnabled
      ? 4
      : 3
    : isMiddleNameInSignUpEnabled
    ? 6
    : 5;
  inputRef.current = _.map(
    Array(totalInputFields),
    (_, i) => (inputRef.current[i] = React.createRef())
  );

  const getScreenTypeOnBackClick = () => {
    if (isNewFamilySignUpFlowEnabled) {
      // 1. for class invite, we don't need to go to verifyParentInviteCode because we have region in the url of class invite
      // 2. for email invite, we navigate user to login screen,
      //       If parent decides to create account after navigating to login screen,
      //       then it will work as normal signup flow because we have cleared the context of invite parent from redux and params
      return isFamilyClassInviteRoute || isFamilyInviteRoute
        ? "parentLogin"
        : "verifyParentInviteCode";
    } else {
      return "parentLogin";
    }
  };

  // onClick functions
  const onBackClick = () => {
    // clear redux state of invite family
    if (isFamilyInviteRoute) {
      updateInvitedFamilyData({});
    }

    updateCurrentScreen({
      type: getScreenTypeOnBackClick(),
      // clear params from url for invite family
      paramsToBeRemoved: isFamilyInviteRoute ? ["metadata", "region"] : [],
    });
    updateInputField({});
  };

  const onSubmitClick = () => {
    const {
      showErrorToast,
      handleCreateParentAccount,
      createInvitedParent,
    } = props;
    const errorCount = isValid();
    showErrorToast({ errorCount });
    if (errorCount > 0) {
      return;
    }

    if (userId) {
      // this function will be called for invited parent (via email)
      createInvitedParent();
    } else {
      // this function will be called for class invite
      // this function will be called for normal signup flow
      handleCreateParentAccount();
    }
  };

  // validation functions
  const isValid = () => {
    let errorCount = 0;
    _.map(Array(totalInputFields), (_, i) => {
      const currentInput = inputRef.current[i].current;

      // skip middle name validation
      if (currentInput.props.name === "middleName") {
        return;
      }
      errorCount += currentInput.isValid();
    });
    return errorCount;
  };

  const checkEmailVerification = value => {
    const { checkEmailVerification } = props;
    return checkEmailVerification(value);
  };

  const passwordValidation = value => {
    if (value.length < 8) {
      return PASSWORD_VALIDATION_TEXT;
    }
    return "";
  };

  const confirmPasswordValidation = value => {
    if (!_.isEqual(password, value)) {
      return "Password and confirm password do not match";
    }
    return "";
  };

  // onEnter Event function
  const onEnterPress = value => {
    if (inputRef.current[value] && inputRef.current[value].current.focus) {
      inputRef.current[value].current.focus();
    }
  };

  const getCurrentAndMaxStep = () => {
    if (isNewFamilySignUpFlowEnabled) {
      return {
        currentStep: isFamilyInviteRoute || isFamilyClassInviteRoute ? null : 2,
        maxStep: isFamilyInviteRoute || isFamilyClassInviteRoute ? null : 2,
      };
    } else {
      return {
        currentStep: null,
        maxStep: null,
      };
    }
  };

  const handleKeyDown = event => {
    if (
      event.key === "Enter" &&
      buttonRef.current &&
      !buttonRef.current.disabled &&
      ![backButtonRef.current, buttonRef.current].includes(
        document.activeElement
      )
    ) {
      buttonRef.current.click();
    }
  };

  const containerStyle = {
    paddingTop: isOrgWorkspaceLoginPage ? "13%" : "19.5%",
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    const region = _.get(invitedFamilyData, "region", null);
    if (!parentInviteCode && !region) {
      updateCurrentScreen({
        type: "verifyParentInviteCode",
      });
    } else if (region) {
      setAuthRegion(region);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div
      className={classes.container}
      style={containerStyle}
      role="main"
      aria-labelledby="create-account-header"
    >
      <div className={classes.contentContainer}>
        <div>
          <div className={classes.headerButtonContainer}>
            {renderBackButtonComponent({
              onClick: onBackClick,
              buttonText: "Family account",
              ...getCurrentAndMaxStep(),
              ariaLabel: "Go back to verify family invite code",
            })}
          </div>
          <div className={classes.headerContainer}>
            <div style={headerStyleV2} id="create-account-header">
              {"Create your Toddle account"}
            </div>
            <div style={subTextStyle}>
              {
                "View and celebrate your child's learning moments, communicate with teachers and receive school updates, all from your Toddle account"
              }
            </div>
          </div>
        </div>
        <div className={classes.inputContainer}>
          {!isMiddleNameInSignUpEnabled ? (
            <FormTextInput
              dsVersion="2.0"
              size={"large"}
              textInputLabel={"Email ID*"}
              value={email}
              name={"email"}
              updateInputField={updateInputField}
              error={"Enter email address"}
              ref={inputRef.current[0]}
              customValidation={checkEmailVerification}
              onEnter={() => onEnterPress(1)}
              disabled={!!userId}
              customErrorClass={classes.customErrorClass}
              placeholder={"Enter your email ID"}
            />
          ) : null}
          <div className={classes.inputRowContainer}>
            <FormTextInput
              dsVersion="2.0"
              size={"large"}
              textInputLabel={"First name*"}
              value={firstName}
              name={"firstName"}
              placeholder={"Enter first name"}
              updateInputField={updateInputField}
              error={"Enter first name"}
              ref={inputRef.current[isMiddleNameInSignUpEnabled ? 0 : 1]}
              onEnter={() => onEnterPress(isMiddleNameInSignUpEnabled ? 1 : 2)}
              customErrorClass={classes.customErrorClass}
              autoFocus={true}
            />
            {isMiddleNameInSignUpEnabled ? (
              <FormTextInput
                dsVersion="2.0"
                size={"large"}
                textInputLabel={"Middle name"}
                value={middleName}
                name={"middleName"}
                placeholder={"Enter middle name"}
                updateInputField={updateInputField}
                ref={inputRef.current[1]}
                onEnter={() => onEnterPress(2)}
              />
            ) : null}
            <FormTextInput
              dsVersion="2.0"
              size={"large"}
              textInputLabel={"Last name*"}
              value={lastName}
              name={"lastName"}
              updateInputField={updateInputField}
              placeholder={"Enter last name"}
              error={"Enter last name"}
              ref={inputRef.current[2]}
              onEnter={() => onEnterPress(3)}
              customErrorClass={classes.customErrorClass}
            />
          </div>
          {isMiddleNameInSignUpEnabled ? (
            <div className={classes.inputRowContainer}>
              <FormTextInput
                dsVersion="2.0"
                size={"large"}
                textInputLabel={"Email ID*"}
                value={email}
                name={"email"}
                placeholder={"Enter your email ID"}
                updateInputField={updateInputField}
                error={"Enter email address"}
                ref={inputRef.current[3]}
                customValidation={checkEmailVerification}
                onEnter={() => onEnterPress(4)}
                disabled={!!userId}
                customErrorClass={classes.customErrorClass}
              />
            </div>
          ) : null}
          {!isPwdDisabledInParentSignup ? (
            <>
              <div className={classes.passwordContainer}>
                <FormTextInput
                  dsVersion="2.0"
                  size={"large"}
                  textInputLabel={"Create a password*"}
                  value={password}
                  name={"password"}
                  updateInputField={updateInputField}
                  type={"password"}
                  error={"Enter password"}
                  ref={inputRef.current[isMiddleNameInSignUpEnabled ? 4 : 3]}
                  onEnter={() =>
                    onEnterPress(isMiddleNameInSignUpEnabled ? 5 : 4)
                  }
                  customValidation={passwordValidation}
                  customErrorClass={classes.customErrorClass}
                />
              </div>
              <div className={classes.passwordContainer}>
                <FormTextInput
                  dsVersion="2.0"
                  size={"large"}
                  textInputLabel={"Confirm password*"}
                  value={confirmPassword}
                  name={"confirmPassword"}
                  updateInputField={updateInputField}
                  type={"password"}
                  ref={inputRef.current[isMiddleNameInSignUpEnabled ? 5 : 4]}
                  customValidation={confirmPasswordValidation}
                  error={"Enter confirm password"}
                  onEnter={onSubmitClick}
                  customErrorClass={classes.customErrorClass}
                />
              </div>
            </>
          ) : null}
        </div>
        <Button
          dsVersion="2.0"
          size="large"
          variant="destructive"
          isFullWidth={true}
          onClick={onSubmitClick}
          ref={buttonRef}
          aria-labelledby="create-account-header"
        >
          {"Continue"}
        </Button>
      </div>

      <div>
        {renderTradeMarkText({
          align: "left",
        })}
      </div>
    </div>
  );
};

export default CreateAccountParent;
